import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { 
  RefreshCw, 
  DollarSign, 
  TrendingUp, 
  Clock, 
  AlertTriangle,
  Plus,
  Minus,
  ArrowUpRight,
  ArrowDownLeft,
  Wallet,
  PiggyBank,
  Users,
  CreditCard,
  Zap
} from "lucide-react";
import { format } from "date-fns";

interface PlatformBalanceData {
  success: boolean;
  balanceSource: string;
  stripeBalanceError: string | null;
  stripeBalance: {
    available: number;
    pending: number;
    total: number;
  };
  pendingProviderPayouts: {
    totalCents: number;
    count: number;
    transactions: Array<{
      provider_amount_cents: number;
      provider_id: string;
      conversation_id: string;
      created_at: string;
    }>;
  };
  netPlatformBalance: {
    cents: number;
    source: string;
  };
  revenue: {
    totalPlatformFeesCents: number;
    totalTransactionVolumeCents: number;
    totalProviderPayoutsCents: number;
    transactionCount: number;
  };
  manualAdjustments: {
    totalCents: number;
    records: Array<{
      id: string;
      amount_cents: number;
      type: string;
      description: string;
      created_at: string;
    }>;
  };
  recentTransactions: Array<{
    id: string;
    amount_cents: number;
    platform_fee_cents: number;
    provider_amount_cents: number;
    status: string;
    transfer_status: string;
    created_at: string;
  }>;
}

const formatCurrency = (cents: number) => {
  const dollars = cents / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(dollars);
};

export const PlatformFinancials = () => {
  const [data, setData] = useState<PlatformBalanceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adjustmentDialogOpen, setAdjustmentDialogOpen] = useState(false);
  const [adjustmentType, setAdjustmentType] = useState<'deposit' | 'withdrawal'>('deposit');
  const [adjustmentAmount, setAdjustmentAmount] = useState('');
  const [adjustmentDescription, setAdjustmentDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sweeping, setSweeping] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const { data: result, error: fnError } = await supabase.functions.invoke('get-platform-balance');
      
      if (fnError) throw fnError;
      if (!result.success) throw new Error(result.error || 'Failed to fetch balance');
      
      setData(result);
    } catch (e: any) {
      setError(e.message);
      toast.error(`Failed to fetch platform balance: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleRecordAdjustment = async () => {
    if (!adjustmentAmount || parseFloat(adjustmentAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setSubmitting(true);
    try {
      const amountCents = Math.round(parseFloat(adjustmentAmount) * 100);
      const signedAmount = adjustmentType === 'withdrawal' ? -amountCents : amountCents;

      // Use edge function to record adjustment (since table may not be in types yet)
      const { data: result, error } = await supabase.functions.invoke('record-platform-adjustment', {
        body: {
          amount_cents: signedAmount,
          type: adjustmentType,
          description: adjustmentDescription || `${adjustmentType === 'deposit' ? 'Deposit' : 'Withdrawal'} recorded by admin`,
        }
      });

      if (error) throw error;
      if (result?.error) throw new Error(result.error);

      toast.success(`${adjustmentType === 'deposit' ? 'Deposit' : 'Withdrawal'} of ${formatCurrency(amountCents)} recorded`);
      setAdjustmentDialogOpen(false);
      setAdjustmentAmount('');
      setAdjustmentDescription('');
      fetchData();
    } catch (e: any) {
      toast.error(`Failed to record adjustment: ${e.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSweepAll = async () => {
    setSweeping(true);
    try {
      const { data: result, error } = await supabase.functions.invoke('process-pending-transfers', {
        body: { sweep_all: true }
      });

      if (error) throw error;
      if (result?.error) throw new Error(result.error);

      toast.success(result.message || 'Sweep completed');
      fetchData();
    } catch (e: any) {
      toast.error(`Sweep failed: ${e.message}`);
    } finally {
      setSweeping(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wallet className="h-5 w-5" />
            Platform Financials
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-24 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            Platform Financials
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-destructive mb-4">{error}</p>
          <Button onClick={fetchData} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Wallet className="h-5 w-5" />
              Platform Financials
            </CardTitle>
            <CardDescription>
              {data?.balanceSource === 'stripe' 
                ? 'Live data from Stripe' 
                : 'Estimated from transaction history'}
              {data?.stripeBalanceError && (
                <span className="text-amber-600 ml-2">(Stripe balance unavailable)</span>
              )}
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Dialog open={adjustmentDialogOpen} onOpenChange={setAdjustmentDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <Plus className="h-4 w-4 mr-1" />
                  Record Adjustment
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Record Balance Adjustment</DialogTitle>
                  <DialogDescription>
                    Record a manual deposit or withdrawal to keep the platform balance accurate.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="flex gap-2">
                    <Button
                      variant={adjustmentType === 'deposit' ? 'default' : 'outline'}
                      onClick={() => setAdjustmentType('deposit')}
                      className="flex-1"
                    >
                      <ArrowDownLeft className="h-4 w-4 mr-2" />
                      Deposit
                    </Button>
                    <Button
                      variant={adjustmentType === 'withdrawal' ? 'default' : 'outline'}
                      onClick={() => setAdjustmentType('withdrawal')}
                      className="flex-1"
                    >
                      <ArrowUpRight className="h-4 w-4 mr-2" />
                      Withdrawal
                    </Button>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="amount">Amount (USD)</Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="amount"
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        className="pl-9"
                        value={adjustmentAmount}
                        onChange={(e) => setAdjustmentAmount(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="description">Description (optional)</Label>
                    <Textarea
                      id="description"
                      placeholder="e.g., Monthly payout from Stripe"
                      value={adjustmentDescription}
                      onChange={(e) => setAdjustmentDescription(e.target.value)}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setAdjustmentDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleRecordAdjustment} disabled={submitting}>
                    {submitting ? 'Recording...' : 'Record Adjustment'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <Button onClick={fetchData} variant="outline" size="sm">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Main Balance Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Net Platform Balance */}
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Net Platform Balance</p>
                  <p className="text-2xl font-bold text-primary">
                    {formatCurrency(data?.netPlatformBalance.cents || 0)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    What's actually yours
                  </p>
                </div>
                <PiggyBank className="h-8 w-8 text-primary/50" />
              </div>
            </CardContent>
          </Card>

          {/* Stripe Total */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Stripe Balance</p>
                  <p className="text-2xl font-bold">
                    {formatCurrency(data?.stripeBalance.total || 0)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {formatCurrency(data?.stripeBalance.available || 0)} available
                  </p>
                </div>
                <CreditCard className="h-8 w-8 text-muted-foreground/50" />
              </div>
            </CardContent>
          </Card>

          {/* Pending Provider Payouts */}
          <Card className={data?.pendingProviderPayouts.count ? "border-amber-500/50" : ""}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Owed to Providers</p>
                  <p className="text-2xl font-bold text-amber-600">
                    {formatCurrency(data?.pendingProviderPayouts.totalCents || 0)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {data?.pendingProviderPayouts.count || 0} pending transfers
                  </p>
                </div>
                <Clock className="h-8 w-8 text-amber-500/50" />
              </div>
            </CardContent>
          </Card>

          {/* Platform Fees Earned */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Fees Earned</p>
                  <p className="text-2xl font-bold text-green-600">
                    {formatCurrency(data?.revenue.totalPlatformFeesCents || 0)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    From {data?.revenue.transactionCount || 0} transactions
                  </p>
                </div>
                <TrendingUp className="h-8 w-8 text-green-500/50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Revenue Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/10 rounded-lg">
                  <DollarSign className="h-5 w-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Volume</p>
                  <p className="text-lg font-semibold">
                    {formatCurrency(data?.revenue.totalTransactionVolumeCents || 0)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500/10 rounded-lg">
                  <Users className="h-5 w-5 text-purple-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Paid to Providers</p>
                  <p className="text-lg font-semibold">
                    {formatCurrency(data?.revenue.totalProviderPayoutsCents || 0)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-500/10 rounded-lg">
                  <TrendingUp className="h-5 w-5 text-green-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Take Rate</p>
                  <p className="text-lg font-semibold">
                    {data?.revenue.totalTransactionVolumeCents 
                      ? ((data.revenue.totalPlatformFeesCents / data.revenue.totalTransactionVolumeCents) * 100).toFixed(1)
                      : '0'}%
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Pending Provider Payouts Detail */}
        {data?.pendingProviderPayouts.count > 0 && (
          <Card className="border-amber-500/30">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Clock className="h-4 w-4 text-amber-500" />
                    Pending Provider Payouts
                  </CardTitle>
                  <CardDescription>
                    These funds are in your Stripe balance but belong to providers awaiting onboarding
                  </CardDescription>
                </div>
                <Button 
                  onClick={handleSweepAll} 
                  disabled={sweeping}
                  size="sm"
                  className="bg-amber-600 hover:bg-amber-700"
                >
                  <Zap className="h-4 w-4 mr-1" />
                  {sweeping ? 'Sweeping...' : 'Sweep All Now'}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Amount</TableHead>
                    <TableHead>Conversation</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.pendingProviderPayouts.transactions.slice(0, 5).map((t, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-medium">
                        {formatCurrency(t.provider_amount_cents)}
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {t.conversation_id?.slice(0, 8)}...
                      </TableCell>
                      <TableCell>
                        {format(new Date(t.created_at), 'MMM d, yyyy')}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-amber-600 border-amber-300">
                          Pending Onboarding
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Recent Transactions */}
        {data?.recentTransactions && data.recentTransactions.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Recent Transactions</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Total</TableHead>
                    <TableHead>Platform Fee</TableHead>
                    <TableHead>Provider</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Transfer</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.recentTransactions.map((t) => (
                    <TableRow key={t.id}>
                      <TableCell className="font-medium">
                        {formatCurrency(t.amount_cents)}
                      </TableCell>
                      <TableCell className="text-green-600">
                        +{formatCurrency(t.platform_fee_cents)}
                      </TableCell>
                      <TableCell>
                        {formatCurrency(t.provider_amount_cents)}
                      </TableCell>
                      <TableCell>
                        <Badge variant={t.status === 'completed' ? 'default' : 'secondary'}>
                          {t.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant="outline"
                          className={
                            t.transfer_status === 'transferred' || t.transfer_status === 'atomic'
                              ? 'text-green-600 border-green-300'
                              : t.transfer_status === 'pending_onboarding'
                              ? 'text-amber-600 border-amber-300'
                              : ''
                          }
                        >
                          {t.transfer_status || 'N/A'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {format(new Date(t.created_at), 'MMM d, h:mm a')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Manual Adjustments History */}
        {data?.manualAdjustments.records && data.manualAdjustments.records.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Manual Adjustments</CardTitle>
              <CardDescription>
                Deposits and withdrawals recorded by admin
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.manualAdjustments.records.map((adj) => (
                    <TableRow key={adj.id}>
                      <TableCell>
                        {adj.amount_cents > 0 ? (
                          <Badge className="bg-green-500">Deposit</Badge>
                        ) : (
                          <Badge variant="destructive">Withdrawal</Badge>
                        )}
                      </TableCell>
                      <TableCell className={adj.amount_cents > 0 ? 'text-green-600' : 'text-red-600'}>
                        {adj.amount_cents > 0 ? '+' : ''}{formatCurrency(adj.amount_cents)}
                      </TableCell>
                      <TableCell className="max-w-xs truncate">
                        {adj.description}
                      </TableCell>
                      <TableCell>
                        {format(new Date(adj.created_at), 'MMM d, yyyy')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </CardContent>
    </Card>
  );
};
